import React from 'react';
import { ValidationError, type AnySchema } from 'yup';
import { buildStrictContractYupSchemaFromFormSchema } from '../../../reactForm/validation';
import { EditorFrame } from './EditorFrame';
import { buildStructureYupSchema } from './structureYupSchema';
import { isRecord } from '../../../../utils/typeGuards';
import { getBindingCatalog } from '../runtime/dataServices/bindingCatalog';
import { createBindingId } from '../runtime/dataServices/bindingId';
import { isRuntimeBindingCompatibleWithContext } from '../runtime/dataServices/bindingCompatibility';
import { supportsSwarmType, type SupportedSwarmTypes } from '../swarmCompatibility';
import { SwarmIncompatibilityNotice } from '../SwarmTypeBadges';
import type {
    BuildStructureSettingsYupSchemaHelpers,
    ContextForKind,
    EditorRenderRuntime,
    ElementContentRenderResult,
    ElementSettingsFormSchema,
    ElementLocation,
    ElementRenderState,
    ElementSettings,
    ResolveElementSettingsFormSchemaArgs,
    RenderContext,
    RenderContextKind,
    ReportElement,
    ReportElementConstructor,
    RenderRuntime,
} from './types';
import type { ActiveDataBinding } from '../runtime/dataServices/types';

function isContextKindAllowed<TKind extends RenderContextKind>(
    compatibleContextKinds: readonly TKind[],
    contextKind: RenderContextKind
): contextKind is TKind {
    return (compatibleContextKinds as readonly RenderContextKind[]).includes(contextKind);
}

function assertMeasuringPointRenderContext(
    ctx: RenderContext,
    elementType: string
): asserts ctx is Extract<RenderContext, { kind: 'measuring-point' }> {
    if (ctx.kind !== 'measuring-point') {
        throw new Error(
            `Element "${elementType}" reported SWARM incompatibility outside a measuring-point render context.`
        );
    }
}

export abstract class BaseReportElement<
    TKind extends RenderContextKind = RenderContextKind,
    TRawSettings extends ElementSettings = ElementSettings,
    TSettings extends ElementSettings = TRawSettings
> implements ReportElement
{
    protected readonly settings: TSettings;

    constructor(rawSettings: TRawSettings) {
        this.settings = this.normalizeSettings(rawSettings);
    }

    protected normalizeSettings(rawSettings: TRawSettings): TSettings {
        this.constructorMetadata.validateSettings(rawSettings);
        const normalizedSettings: ElementSettings = rawSettings;
        return normalizedSettings as TSettings;
    }

    // TypeScript currently does not infer subclass static fields from `this.constructor`
    // in a way we can safely consume here, so we centralize a single cast and keep
    // element subclasses free of repetitive metadata plumbing.
    // If TS gains better static-side inheritance typing for `this.constructor`, remove this.
    private get constructorMetadata(): ReportElementConstructor<string, TKind> {
        return this.constructor as ReportElementConstructor<string, TKind>;
    }

    static resolveSettingsFormSchema(
        this: ReportElementConstructor,
        {
            contextKind,
            context,
            client,
            runtimeDataServices,
            currentSettings,
        }: ResolveElementSettingsFormSchemaArgs
    ): Promise<ElementSettingsFormSchema> {
        // Base implementation is intentionally context/runtime agnostic.
        void contextKind;
        void context;
        void client;
        void runtimeDataServices;
        void currentSettings;

        if (!this.settingsFormSchema) {
            throw new Error(`Missing settingsFormSchema for "${this.elementType}".`);
        }

        return Promise.resolve(this.settingsFormSchema);
    }

    static buildStructureSettingsYupSchema(
        this: ReportElementConstructor,
        helpers: BuildStructureSettingsYupSchemaHelpers
    ): AnySchema {
        if (!this.settingsFormSchema) {
            throw new Error(
                `Missing settingsFormSchema for "${this.elementType}" structure schema build.`
            );
        }

        return buildStrictContractYupSchemaFromFormSchema(this.settingsFormSchema, {
            buildDefinitionsSchema: helpers.buildDefinitionsSchema,
        })
            .required()
            .noUnknown();
    }

    static validateSettings(this: ReportElementConstructor, rawSettings: ElementSettings): void {
        const contextKind = this.compatibleContextKinds[0];
        if (!contextKind) {
            throw new Error(
                `Expected "${this.elementType}" to define at least one compatible context kind.`
            );
        }

        if (!isRecord(rawSettings)) {
            throw new Error(`Expected "${this.elementType}" settings to be an object.`);
        }

        try {
            buildStructureYupSchema({
                target: 'element-settings',
                contextKind,
                elementClasses: [this],
                definition: {
                    type: this.elementType,
                    settings: rawSettings,
                },
            }).validateSync(rawSettings, {
                abortEarly: false,
                strict: true,
            });
        } catch (error) {
            if (error instanceof ValidationError) {
                throw new Error(error.errors[0] ?? error.message);
            }

            throw error;
        }
    }

    get type(): string {
        return this.constructorMetadata.elementType;
    }

    get compatibleContextKinds(): readonly TKind[] {
        return this.constructorMetadata.compatibleContextKinds;
    }

    isCompatibleWith(ctx: RenderContext): ctx is ContextForKind<TKind> {
        return isContextKindAllowed(this.compatibleContextKinds, ctx.kind);
    }

    protected getSupportedSwarmTypes(): SupportedSwarmTypes {
        return null;
    }

    protected getRuntimeBindings(): readonly ActiveDataBinding[] {
        return [];
    }

    isSwarmCompatibleWith(ctx: RenderContext): boolean {
        const supportedSwarmTypes = this.getSupportedSwarmTypes();
        if (supportedSwarmTypes !== null) {
            if (ctx.kind !== 'measuring-point') {
                throw new Error(
                    `Element "${this.type}" has SWARM-specific content outside a measuring-point render context.`
                );
            }
            if (!supportsSwarmType(supportedSwarmTypes, ctx.measuringPoint.swarmType)) {
                return false;
            }
        }

        const runtimeBindings = this.getRuntimeBindings();
        if (runtimeBindings.length === 0) {
            return true;
        }

        const bindingCatalog = getBindingCatalog();
        return runtimeBindings.every((binding) => {
            const descriptor = bindingCatalog.getByIdOrThrow(createBindingId(binding));
            return isRuntimeBindingCompatibleWithContext(
                descriptor.bindingId,
                descriptor.link,
                ctx
            );
        });
    }

    protected assertCompatible(ctx: RenderContext): asserts ctx is ContextForKind<TKind> {
        if (this.isCompatibleWith(ctx)) {
            return;
        }

        throw new Error(
            `Element "${this.type}" is not compatible with "${ctx.kind}" render context.`
        );
    }

    private getCurrentLocation(runtime: RenderRuntime): ElementLocation {
        const location = runtime.getCurrentElementLocation();
        if (location) {
            return location;
        }

        throw new Error(
            `Missing render location for element "${this.type}". Render through renderDefinitionList(...) to enable editor actions.`
        );
    }

    toReactNode(ctx: RenderContext, runtime: RenderRuntime): ElementContentRenderResult {
        this.assertCompatible(ctx);
        const isSwarmCompatible = this.isSwarmCompatibleWith(ctx);
        if (!isSwarmCompatible && !runtime.showEditor) {
            return {
                node: null,
                ready: Promise.resolve(),
                hasRenderableContent: Promise.resolve(false),
            };
        }

        const content = this.toCompatibleReactNode(ctx, runtime, {
            isSwarmCompatible,
        });
        return this.wrapWithEditorFrame(content, runtime, ctx, isSwarmCompatible);
    }

    private wrapWithEditorFrame(
        rendered: ElementContentRenderResult,
        runtime: RenderRuntime,
        ctx: RenderContext,
        isSwarmCompatible: boolean
    ): ElementContentRenderResult {
        if (!runtime.showEditor) {
            return rendered;
        }

        const editorRuntime = runtime;

        if (!this.shouldApplyEditorFrame(editorRuntime)) {
            return rendered;
        }

        let notice: React.ReactNode = null;
        if (!isSwarmCompatible && this.shouldRenderFrameIncompatibilityNotice()) {
            assertMeasuringPointRenderContext(ctx, this.type);
            notice = React.createElement(SwarmIncompatibilityNotice, {
                swarmType: ctx.measuringPoint.swarmType,
            });
        }

        return {
            node: React.createElement(
                EditorFrame,
                {
                    elementType: this.type,
                    runtime: editorRuntime,
                    location: this.getCurrentLocation(editorRuntime),
                    renderContext: ctx,
                    containerClassName: this.getEditorFrameContainerClassName(editorRuntime),
                    canEditSettings: Boolean(this.constructorMetadata.settingsFormSchema),
                },
                notice,
                rendered.node
            ),
            ready: rendered.ready,
            hasRenderableContent: rendered.hasRenderableContent,
        };
    }

    protected shouldApplyEditorFrame(_runtime: EditorRenderRuntime): boolean {
        return true;
    }

    protected shouldRenderFrameIncompatibilityNotice(): boolean {
        return true;
    }

    // EditorFrame wraps the rendered element and therefore participates in layout.
    // Keep the default wrapper width-bounded so oversized children cannot widen the
    // preview container and break page scaling/centering calculations.
    protected getEditorFrameContainerClassName(_runtime: EditorRenderRuntime): string {
        return 'relative max-w-full';
    }

    protected abstract toCompatibleReactNode(
        ctx: ContextForKind<TKind>,
        runtime: RenderRuntime,
        renderState: ElementRenderState
    ): ElementContentRenderResult;
}
